/**
 * CHALLENGE — la vista de la mesa para la interrupción en curso (§6.2, t2-t8).
 *
 * El pedido nace en el banco (intent) y la mesa lo acepta en la consola: eso
 * ya existe. Lo que NO existía es el final del cuento: un challenge aceptado
 * dejaba la interrupción abierta (`CHALLENGE_IN_PROGRESS`) hasta cargar el
 * `challenge_resolved` con curl. Esta pantalla es ese final:
 *
 *  - **el motivo** (art. 8, segundo paso): si el pedido entró sin falta, acá
 *    se registra la elegida como `correction` sobre el request;
 *  - **el desenlace** (art. 25): los cinco outcomes del VCS, con la retención
 *    derivada del reglamento — no es una decisión de la mesa;
 *  - **la reconciliación** (BVB 5.10): CONFIRM / INVERT / DELETE_LAST_POINT /
 *    REPLAY, con la advertencia dicha ANTES de apretar: INVERT cambia el
 *    saque, DELETE lo devuelve al mismo sacador, y equivocarse rompe la
 *    rotación del resto del set.
 *
 * Todo sale en UN `POST /events` (`buildResolutionBatch`): o entra la
 * resolución CON su efecto en el marcador, o no entra nada. El botón grande
 * queda apagado hasta que el desenlace Y la acción estén elegidos — bajo
 * presión, la pantalla no deja mandar la mitad.
 *
 * La duración de la revisión se cronometra desde que esta pantalla ve el
 * pedido abierto (sin tope: el reglamento no lo tiene y ponerlo sería
 * antirreglamentario — §6.1). Si la mesa abrió la pestaña tarde, el reloj
 * cuenta de menos; es un piso honesto, no una medición del VAR.
 */
import { useEffect, useRef, useState } from 'react';
import type { ChallengeFault, ChallengeOutcome, ScoreAction } from '@scout/core';
import { useT } from '../../i18n/use-t';
import type { MessageKey } from '../../i18n/index';
import { Card, Empty, ErrorBox } from '../../components/States';
import { errorMessage } from '../../hooks';
import { timecode } from '../../lib/bench';
import { freshLastSeq, type MesaTabProps } from './MesaPage';
import {
  MESA_CHALLENGE_FAULTS,
  MESA_CHALLENGE_OUTCOMES,
  MESA_SCORE_ACTIONS,
  buildResolutionBatch,
  faultCorrectionEvent,
  menuRequiresUnsuccessful,
  mesaActor,
  remainingAfterResolution,
  retainsChallenge,
} from './mesa';

function faultKey(fault: string): MessageKey {
  return `mesa.challenge.faults.${fault}` as MessageKey;
}

function outcomeKey(outcome: ChallengeOutcome): MessageKey {
  return `mesa.challenge.outcomes.${outcome}` as MessageKey;
}

const PLAN_KEYS: Record<ScoreAction, MessageKey> = {
  CONFIRM: 'mesa.challenge.planConfirm',
  INVERT: 'mesa.challenge.planInvert',
  DELETE_LAST_POINT: 'mesa.challenge.planDelete',
  REPLAY: 'mesa.challenge.planReplay',
  NONE: 'mesa.challenge.planConfirm',
};

export function ChallengeTab(props: MesaTabProps): JSX.Element {
  const { client, matchId, sheet, snapshot, identity, reload } = props;
  const t = useT();

  const open = sheet.challenge.open;
  const closed = sheet.document.closed;

  const [outcome, setOutcome] = useState<ChallengeOutcome | null>(null);
  const [action, setAction] = useState<ScoreAction | null>(null);
  const [observed, setObserved] = useState('');
  const [report, setReport] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Cronómetro de la revisión: arranca cuando ESTA pantalla ve el pedido y se
  // resetea si cambia el pedido (otro challenge en la misma interrupción).
  const reviewStart = useRef<number>(Date.now());
  const openId = open?.requestEventId ?? null;
  useEffect(() => {
    reviewStart.current = Date.now();
    setOutcome(null);
    setAction(null);
    setObserved('');
    setReport('');
    setNotice(null);
  }, [openId]);

  // La ventana del art. 7 (7 s desde el punto). Su reloj es el maestro del
  // video, que el navegador no conoce: se cuenta desde que ESTA pantalla vio
  // el punto nuevo (mismo criterio honesto que la revisión: un piso, no una
  // medición del VAR). El refresco de la mesa lo trae en 3 s o menos.
  const win = sheet.challenge.window;
  const winId = win?.pointEventId ?? null;
  const winSeen = useRef<number>(Date.now());
  useEffect(() => {
    winSeen.current = Date.now();
  }, [winId]);

  // Tic para los relojes visibles; no toca la red. Más fino mientras corre la
  // ventana (se ve bajar el arco), de a un segundo durante la revisión.
  const [, setTick] = useState(0);
  const windowRunning = winId !== null && openId === null;
  useEffect(() => {
    if (openId === null && !windowRunning) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), windowRunning ? 250 : 1000);
    return () => clearInterval(timer);
  }, [openId, windowRunning]);

  const onlyUnsuccessful = menuRequiresUnsuccessful(sheet.challenge);

  const pickFault = async (fault: ChallengeFault): Promise<void> => {
    if (open === null) return;
    setBusy(true);
    setError(null);
    try {
      const event = faultCorrectionEvent(open.requestEventId, fault, {
        matchId,
        seq: (await freshLastSeq(client, matchId)) + 1,
        actor: mesaActor(identity?.id ?? 'mesa'),
      });
      await client.appendEvents(matchId, [event]);
      reload();
    } catch (err) {
      setError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (): Promise<void> => {
    if (open === null || outcome === null || action === null) return;
    setBusy(true);
    setError(null);
    try {
      // El log entero: la reconciliación necesita el último punto VIGENTE
      // (con undos y correcciones resueltos), no un contador.
      const page = await client.getEvents(matchId);
      const batch = buildResolutionBatch({
        matchId,
        log: page.events,
        challenge: sheet.challenge,
        outcome,
        scoreAction: action,
        observedFault: observed.trim() === '' ? null : observed.trim(),
        ...(report.trim() !== '' ? { challengeRefereeReport: report.trim() } : {}),
        reviewDurationMs: Date.now() - reviewStart.current,
        actor: mesaActor(identity?.id ?? 'mesa'),
      });
      if ('error' in batch) {
        setError(
          batch.error === 'no_point'
            ? t('mesa.challenge.noPoint')
            : batch.error === 'menu_requires_unsuccessful'
              ? t('mesa.challenge.onlyUnsuccessful')
              : t('mesa.challenge.none'),
        );
        return;
      }
      await client.appendEvents(matchId, batch.events);
      setNotice(t('mesa.challenge.resolvedOk'));
      reload();
    } catch (err) {
      setError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(false);
    }
  };

  const remaining = sheet.challenge.remaining;
  const retained = outcome !== null ? retainsChallenge(outcome) : null;
  const after =
    outcome !== null && open !== null
      ? remainingAfterResolution(sheet.challenge, retained === true)
      : null;

  const reviewSeconds = Math.max(0, Math.floor((Date.now() - reviewStart.current) / 1000));
  const windowSeconds =
    win !== null ? Math.max(1, (win.closesAtNs - win.opensAtNs) / NS_PER_SEC) : DEFAULT_WINDOW_SEC;
  const windowLeft =
    win !== null ? Math.max(0, windowSeconds - (Date.now() - winSeen.current) / 1000) : 0;

  const step = flowStep({
    open: open !== null,
    hasFault: open !== null && open.claimedFault !== null,
    onlyUnsuccessful,
    outcome: outcome !== null,
    action: action !== null,
  });

  return (
    <div>
      <Card
        title={t('mesa.challenge.title')}
        actions={
          <span className="chip muted">
            {t('mesa.challenge.remainingNow', { home: remaining.home, away: remaining.away })}
          </span>
        }
      >
        <ol className="flow" aria-label={t('mesaPanel.flowLabel')}>
          {FLOW_STEPS.map((name, index) => (
            <li
              key={name}
              className={index < step ? 'done' : index === step ? 'current' : undefined}
              aria-current={index === step ? 'step' : undefined}
            >
              <b>{t(`mesaPanel.flow.${name}` as MessageKey)}</b>
              <span>{t(`mesaPanel.flowHint.${name}` as MessageKey)}</span>
            </li>
          ))}
        </ol>

        <div className="clock-row">
          <WindowClock
            seconds={windowSeconds}
            left={win !== null && open === null ? windowLeft : 0}
            state={open !== null ? 'review' : win === null ? 'none' : windowLeft > 0 ? 'open' : 'closed'}
          />
          {open !== null ? (
            <div className="clock review" role="timer" aria-live="off">
              <span className="clock-digits-big">{formatClock(reviewSeconds)}</span>
              <span className="clock-text">
                <b>{t('mesaPanel.reviewTitle')}</b>
                <span>{t('mesa.challenge.reviewSeconds', { seconds: reviewSeconds })}</span>
              </span>
            </div>
          ) : null}
        </div>

        {error !== null ? <ErrorBox message={error} /> : null}
        {notice !== null ? <p className="chip ok">{notice}</p> : null}

        {open === null ? (
          <>
            {sheet.challenge.window !== null ? (
              <p className="chip warn">{t('mesa.challenge.windowOpen')}</p>
            ) : null}
            <Empty>{t('mesa.challenge.none')}</Empty>
          </>
        ) : (
          <div>
            <div className="row" style={{ marginBottom: 8 }}>
              <strong
                className={open.team !== null ? `side-name ${open.team}` : undefined}
                style={{ fontSize: '1.35rem', fontFamily: 'var(--font-display)' }}
              >
                {open.team !== null
                  ? t('mesa.challenge.requestedBy', {
                      team: t(`mesa.team.${open.team}` as MessageKey),
                    })
                  : t('mesa.challenge.requestedByRef')}
              </strong>
              <span className={`chip ${open.status === 'UNDER_REVIEW' ? 'warn' : 'bad'}`}>
                {t(`mesa.challenge.status.${open.status}` as MessageKey)}
              </span>
            </div>
            <p className="hint" style={{ marginTop: 0 }}>
              {snapshot !== null
                ? `${t('mesa.challenge.setScore', {
                    set: snapshot.currentSet,
                    score: `${snapshot.teams.home.score}:${snapshot.teams.away.score}`,
                  })} · `
                : ''}
              {t('mesa.challenge.at', { tc: timecode(open.requestedAtNs) })}
            </p>
            <p style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {open.claimedFault !== null
                ? t('mesa.challenge.claimed', { fault: t(faultKey(open.claimedFault)) })
                : t('mesa.challenge.claimedNone')}
            </p>

            {open.claimedFault === null ? (
              <div style={{ marginBottom: 14 }}>
                <h3>{t('mesa.challenge.pickFault')}</h3>
                <div className="fault-grid">
                  {MESA_CHALLENGE_FAULTS.map((fault) => (
                    <button
                      key={fault}
                      type="button"
                      disabled={busy || closed}
                      onClick={() => void pickFault(fault)}
                    >
                      {t(faultKey(fault))}
                    </button>
                  ))}
                </div>
                <p className="hint">{t('mesa.challenge.onlyUnsuccessful')}</p>
              </div>
            ) : null}

            <h3>{t('mesa.challenge.resolveTitle')}</h3>
            <div className="outcome-grid" role="group" aria-label={t('mesa.challenge.resolveTitle')}>
              {MESA_CHALLENGE_OUTCOMES.map((o) => {
                const blocked = onlyUnsuccessful && o !== 'UNSUCCESSFUL';
                return (
                  <button
                    key={o}
                    type="button"
                    disabled={busy || closed || blocked}
                    title={t(`mesa.challenge.outcomeHints.${o}` as MessageKey)}
                    aria-pressed={outcome === o}
                    className={`outcome-btn ${OUTCOME_TONE[o]}`}
                    onClick={() => setOutcome(o)}
                  >
                    {t(outcomeKey(o))}
                    <small>
                      {retainsChallenge(o) ? t('mesaPanel.keeps') : t('mesaPanel.loses')}
                    </small>
                  </button>
                );
              })}
            </div>
            {outcome !== null ? (
              <p>
                <span className={`chip ${retained === true ? 'ok' : 'bad'}`}>
                  {retained === true ? t('mesa.challenge.retained') : t('mesa.challenge.lost')}
                </span>{' '}
                <span className="hint">
                  {t(`mesa.challenge.outcomeHints.${outcome}` as MessageKey)}
                </span>
              </p>
            ) : null}

            <div className="grid-2">
              <label className="field">
                <span>{t('mesa.challenge.observed')}</span>
                <input value={observed} onChange={(e) => setObserved(e.target.value)} />
              </label>
              <label className="field">
                <span>{t('mesa.challenge.report')}</span>
                <input value={report} onChange={(e) => setReport(e.target.value)} />
              </label>
            </div>

            <h3>{t('mesa.challenge.scoreTitle')}</h3>
            <p className="hint" style={{ marginTop: 0 }}>
              {t('mesa.challenge.scoreExplain')}
            </p>
            <div className="outcome-grid" role="group" aria-label={t('mesa.challenge.scoreTitle')}>
              {MESA_SCORE_ACTIONS.map((a) => (
                <button
                  key={a}
                  type="button"
                  disabled={busy || closed}
                  title={t(`mesa.challenge.actionHints.${a}` as MessageKey)}
                  aria-pressed={action === a}
                  className={`outcome-btn compact ${ACTION_TONE[a]}`}
                  onClick={() => setAction(a)}
                >
                  {t(`mesa.challenge.actions.${a}` as MessageKey)}
                  <small>{t(`mesa.challenge.actionHints.${a}` as MessageKey)}</small>
                </button>
              ))}
            </div>
            {action !== null ? (
              <p style={{ fontWeight: 600 }}>
                {t(PLAN_KEYS[action], {
                  team:
                    open.team !== null
                      ? t(`mesa.team.${open.team === 'home' ? 'away' : 'home'}` as MessageKey)
                      : '',
                })}
                {after !== null
                  ? ` ${t('mesa.challenge.afterRemaining', { home: after.home, away: after.away })}`
                  : ''}
              </p>
            ) : null}

            <div className="confirm-bar">
              <span className="hint">
                {outcome === null || action === null ? t('mesaPanel.confirmNeeds') : ''}
              </span>
              <span className="spacer" />
              <button
                type="button"
                className="primary big"
                disabled={busy || closed || outcome === null || action === null}
                onClick={() => void resolve()}
              >
                {busy ? '…' : t('mesa.challenge.confirm')}
              </button>
            </div>
          </div>
        )}
      </Card>

      <Card title={t('mesa.challenge.logTitle')}>
        {sheet.challenge.resolutions.length === 0 ? (
          <Empty>{t('mesa.challenge.logEmpty')}</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <tbody>
                {sheet.challenge.resolutions.map((r) => (
                  <tr key={r.requestEventId}>
                    <td>
                      {r.team !== null
                        ? t(`mesa.team.${r.team}` as MessageKey)
                        : t('mesa.roles.referee1')}
                    </td>
                    <td>
                      <span className={`outcome-dot ${OUTCOME_TONE[r.outcome]}`} />
                      {t(outcomeKey(r.outcome))}
                    </td>
                    <td>
                      <span className={`chip ${r.retained ? 'ok' : 'bad'}`}>
                        {r.retained ? t('mesa.challenge.logRetained') : t('mesa.challenge.logLost')}
                      </span>
                    </td>
                    <td>
                      {r.scoreAction !== 'NONE'
                        ? t(`mesa.challenge.actions.${r.scoreAction}` as MessageKey)
                        : '—'}
                    </td>
                    <td className="hint num">{Math.round(r.reviewDurationMs / 1000)} s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// El flujo y los relojes
// ---------------------------------------------------------------------------

const NS_PER_SEC = 1_000_000_000;

/** `requestWindowSec` por defecto de `SportConfig` (art. 7). */
const DEFAULT_WINDOW_SEC = 7;

const FLOW_STEPS = ['request', 'reason', 'outcome', 'score', 'confirm'] as const;

/** En qué paso del flujo está la interrupción (índice de `FLOW_STEPS`). */
export function flowStep(input: {
  open: boolean;
  hasFault: boolean;
  onlyUnsuccessful: boolean;
  outcome: boolean;
  action: boolean;
}): number {
  if (!input.open) return 0;
  if (!input.hasFault && !input.onlyUnsuccessful && !input.outcome) return 1;
  if (!input.outcome) return 2;
  if (!input.action) return 3;
  return 4;
}

/** El color de cada desenlace es el de la evaluación equivalente (igual en todo el producto). */
const OUTCOME_TONE: Record<ChallengeOutcome, string> = {
  SUCCESSFUL: 'eval-perfect',
  UNSUCCESSFUL: 'eval-error',
  INCONCLUSIVE: 'eval-neutral',
  DECLINED_UNNECESSARY: 'eval-positive',
  FOOTAGE_UNAVAILABLE: 'eval-blocked',
};

/** Las acciones que tocan el marcador van en colores de riesgo; confirmar, en el acento. */
const ACTION_TONE: Record<ScoreAction, string> = {
  CONFIRM: '',
  INVERT: 'eval-blocked',
  DELETE_LAST_POINT: 'eval-error',
  REPLAY: 'eval-neutral',
  NONE: '',
};

function formatClock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** El reloj de la ventana del art. 7: un arco que se vacía en 7 s. */
function WindowClock({
  seconds,
  left,
  state,
}: {
  seconds: number;
  left: number;
  state: 'none' | 'open' | 'closed' | 'review';
}): JSX.Element {
  const t = useT();
  const r = 26;
  const c = 2 * Math.PI * r;
  const fraction = state === 'open' ? Math.min(1, left / seconds) : 0;
  const shown = state === 'open' ? Math.ceil(left) : 0;
  const tone = state === 'open' ? (left <= 2 ? ' urgent' : '') : ' closed';
  return (
    <div className={`clock${tone}`} role="timer" aria-live="off">
      <svg width={64} height={64} viewBox="0 0 64 64" aria-hidden="true">
        <circle className="clock-track" cx={32} cy={32} r={r} />
        <circle
          className="clock-arc"
          cx={32}
          cy={32}
          r={r}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - fraction)}
          transform="rotate(-90 32 32)"
        />
        <text className="clock-digits" x={32} y={40} textAnchor="middle">
          {shown}
        </text>
      </svg>
      <span className="clock-text">
        <b>{t('mesaPanel.windowTitle', { seconds: Math.round(seconds) })}</b>
        <span>
          {state === 'open'
            ? t('mesaPanel.windowOpen', { seconds: shown })
            : state === 'closed'
              ? t('mesaPanel.windowClosed')
              : state === 'review'
                ? t('mesaPanel.windowReview')
                : t('mesaPanel.windowNone')}
        </span>
        <span>{t('mesaPanel.windowHint')}</span>
      </span>
    </div>
  );
}
